import React, { useEffect, useMemo, useState } from 'react';
import { TableDataAPI } from '../../../Api/TableDataAPI';

const initialForm = {
  targetUserId: '',
  bloque: '',
  barco: '',
  lance: '',
  empresa: '',
  corral: '',
  pesoKs: '',
};

const bloqueOptions = ['BLOQUE #1', 'BLOQUE #2'];
const barcoOptions = ['Buenaventura 1', 'Buenaventura 2'];
const empresaOptions = ['BAF', 'SER'];

const readJsonFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => {
    try {
      resolve(JSON.parse(reader.result));
    } catch (error) {
      reject(new Error('Invalid JSON file.'));
    }
  };

  reader.onerror = () => reject(new Error('Could not read JSON file.'));
  reader.readAsText(file);
});

const getTransferVideoCount = (jsonData) => {
  if (!jsonData || !Array.isArray(jsonData.transfer_videos)) return 0;
  return jsonData.transfer_videos.length;
};

const TableImportPanel = ({ onImportSuccess, onTargetUserChange }) => {
  const [targetUsers, setTargetUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const rowCount = useMemo(() => getTransferVideoCount(jsonData), [jsonData]);

  useEffect(() => {
    let mounted = true;

    TableDataAPI.getTargetUsers()
      .then((response) => {
        if (mounted) setTargetUsers(response.data || []);
      })
      .catch((requestError) => {
        console.error(requestError);
        if (mounted) setError('Could not load target users.');
      })
      .finally(() => {
        if (mounted) setLoadingUsers(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'targetUserId') {
      onTargetUserChange?.(value ? Number(value) : null);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setMessage('');
    setError('');
    setJsonFile(null);
    setJsonData(null);

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Only JSON files are supported.');
      event.target.value = '';
      return;
    }

    try {
      const parsed = await readJsonFile(file);
      const parsedRowCount = getTransferVideoCount(parsed);

      if (parsedRowCount === 0) {
        throw new Error('JSON must contain transfer_videos rows.');
      }

      setJsonFile(file);
      setJsonData(parsed);
      setMessage(`${parsedRowCount} rows ready to import.`);
    } catch (parseError) {
      setError(parseError.message);
      event.target.value = '';
    }
  };

  const isSubmitDisabled = (
    submitting ||
    !jsonFile ||
    rowCount === 0 ||
    !form.targetUserId ||
    !form.bloque ||
    !form.barco ||
    !form.lance ||
    !form.empresa ||
    !form.corral ||
    !form.pesoKs
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (isSubmitDisabled) {
      setError('Complete all fields and choose a valid JSON file.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await TableDataAPI.importRows({
        targetUserId: Number(form.targetUserId),
        fileName: jsonFile.name,
        sourceType: 'transfer_videos',
        rawJson: JSON.stringify(jsonData),
        selected: {
          bloque: form.bloque,
          barco: form.barco,
          lance: form.lance,
          empresa: form.empresa,
          corral: form.corral,
          pesoKs: Number(form.pesoKs),
        },
      });

      setMessage(`${response.data.rowCount} rows imported.`);
      setJsonFile(null);
      setJsonData(null);
      setForm((prev) => ({ ...initialForm, targetUserId: prev.targetUserId }));
      onImportSuccess?.(response.data);
    } catch (submitError) {
      const errorMessage =
        submitError?.response?.data?.message ||
        submitError?.message ||
        'Import failed.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="table-import-panel">
      <div className="table-import-header">
        <h2>JSON import</h2>
        <span>{rowCount > 0 ? `${rowCount} rows` : 'Admin only'}</span>
      </div>

      <form className="table-import-form" onSubmit={handleSubmit}>
        <label>
          Target user
          <select
            name="targetUserId"
            value={form.targetUserId}
            onChange={handleFormChange}
            disabled={loadingUsers || submitting}
            required
          >
            <option value="">{loadingUsers ? 'Loading users...' : 'Select user'}</option>
            {targetUsers.map((user) => (
              <option key={user.Id} value={user.Id}>
                {user.companyName || user.name || user.email} #{user.Id}
              </option>
            ))}
          </select>
        </label>

        <label>
          Bloque
          <select name="bloque" value={form.bloque} onChange={handleFormChange} required>
            <option value="">Select bloque</option>
            {bloqueOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <label>
          Barco
          <select name="barco" value={form.barco} onChange={handleFormChange} required>
            <option value="">Select barco</option>
            {barcoOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <label>
          Lance
          <input
            name="lance"
            type="text"
            value={form.lance}
            onChange={handleFormChange}
            placeholder="L1"
            required
          />
        </label>

        <label>
          Empresa
          <select name="empresa" value={form.empresa} onChange={handleFormChange} required>
            <option value="">Select empresa</option>
            {empresaOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <label>
          Corral
          <input
            name="corral"
            type="text"
            value={form.corral}
            onChange={handleFormChange}
            placeholder="CC78"
            required
          />
        </label>

        <label>
          Peso (kg)
          <input
            name="pesoKs"
            type="number"
            min="0.01"
            step="0.01"
            value={form.pesoKs}
            onChange={handleFormChange}
            required
          />
        </label>

        <label>
          JSON file
          <input
            type="file"
            accept="application/json,.json"
            onChange={handleFileChange}
            required
          />
        </label>

        <button type="submit" disabled={isSubmitDisabled}>
          {submitting ? 'Importing...' : 'Append rows'}
        </button>
      </form>

      {(message || error) && (
        <div className={`table-import-status ${error ? 'error' : 'success'}`}>
          {error || message}
        </div>
      )}
    </section>
  );
};

export default TableImportPanel;
